import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ChevronRight, FileText, Phone, Download, ArrowRight, Zap, Thermometer, Gauge, Weight, Ruler, Shield, Settings, Cpu } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function SpecRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <tr>
      <th className="text-left py-2 px-3 text-sm font-medium text-muted-foreground whitespace-nowrap align-top">{label}</th>
      <td className="py-2 px-3 text-sm" style={{ color: 'var(--brand-navy)' }}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</td>
    </tr>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/products/${slug}`)
      .then(r => setProduct(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <Skeleton className="h-8 w-3/4 mb-4" /><Skeleton className="h-4 w-1/2 mb-8" />
      <div className="grid md:grid-cols-2 gap-8"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>
    </div>
  );
  if (!product) return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 text-center">
      <h1 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Product Not Found</h1>
      <Link to="/products"><Button variant="outline">Back to Products</Button></Link>
    </div>
  );

  const specs = product.specs || {};
  const summary = product.summary || {};

  return (
    <div className="page-transition">
      {/* Breadcrumb */}
      <div className="border-b" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-[var(--brand-navy)]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/products" className="hover:text-[var(--brand-navy)]">Products</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to={`/products/${product.category}`} className="hover:text-[var(--brand-navy)]">{product.category?.replace(/-/g, ' ')}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="truncate">{specs.model_code}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="py-8 sm:py-12" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <Badge className="mb-3" style={{ background: 'rgba(14,138,138,0.1)', color: 'var(--brand-teal)' }}>{product.category?.replace(/-/g, ' ')}</Badge>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{product.name}</h1>
              <p className="text-muted-foreground mb-6">{product.tagline}</p>
              
              {/* Key Metrics */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {[
                  { icon: Zap, label: 'Capacity', value: `${specs.nominal_capacity_kw} kW / ${specs.nominal_capacity_tr} TR` },
                  { icon: Gauge, label: 'COP', value: specs.cop },
                  { icon: Thermometer, label: 'Refrigerant', value: specs.refrigerant },
                ].map((m, i) => (
                  <Card key={i} style={{ background: 'var(--surface-1)' }}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <m.icon className="w-3.5 h-3.5" style={{ color: 'var(--brand-teal)' }} />
                        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">{m.label}</span>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: 'var(--brand-navy)', fontFamily: 'Space Grotesk' }}>{m.value}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3">
                <Link to={`/request-quote?model=${specs.model_code}`}>
                  <Button data-testid="product-request-quote" className="text-white" style={{ background: 'var(--brand-teal)' }}>
                    <FileText className="w-4 h-4 mr-2" /> Request a Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline"><Phone className="w-4 h-4 mr-2" /> Contact Sales</Button>
                </Link>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden bg-[var(--surface-2)] flex items-center justify-center p-8">
              {product.image_url ? (
                <img src={product.image_url} alt={product.name} className="w-full h-auto max-h-80 object-contain rounded" loading="lazy" />
              ) : (
                <div className="text-center text-muted-foreground">
                  <Settings className="w-16 h-16 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Product Image</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="border-t" style={{ background: 'var(--surface-1)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><span className="text-xs text-muted-foreground uppercase">Capacity Range</span><p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{summary.capacity_range}</p></div>
            <div><span className="text-xs text-muted-foreground uppercase">Temp Range</span><p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{summary.temperature_range}</p></div>
            <div><span className="text-xs text-muted-foreground uppercase">Compressor</span><p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{summary.compressor_make} ({summary.compressor_type})</p></div>
            <div><span className="text-xs text-muted-foreground uppercase">Refrigerant</span><p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{summary.refrigerant_options?.join(', ')}</p></div>
          </div>
          {summary.applications && (
            <div className="mt-4">
              <span className="text-xs text-muted-foreground uppercase">Applications</span>
              <div className="flex flex-wrap gap-1 mt-1">{summary.applications.map(a => <Badge key={a} variant="outline" className="text-xs">{a}</Badge>)}</div>
            </div>
          )}
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Tabs defaultValue="specs" data-testid="product-specs-tabs">
            <TabsList className="w-full justify-start flex-wrap gap-1 bg-transparent border-b rounded-none pb-0 h-auto">
              <TabsTrigger value="specs" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)]">A. Technical Specs</TabsTrigger>
              <TabsTrigger value="options" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)]">B. Options</TabsTrigger>
              <TabsTrigger value="materials" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)]">C. Materials</TabsTrigger>
              <TabsTrigger value="downloads" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)]">D. Downloads</TabsTrigger>
              <TabsTrigger value="inquiry" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)]">E. Inquiry</TabsTrigger>
              <TabsTrigger value="related" className="rounded-b-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--brand-teal)]">F. Related</TabsTrigger>
            </TabsList>

            {/* Tab A: Technical Specifications */}
            <TabsContent value="specs" className="mt-6">
              <Accordion type="multiple" defaultValue={['general','compressor','heatex','performance','physical','controls']}>
                <AccordionItem value="general">
                  <AccordionTrigger className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>General</AccordionTrigger>
                  <AccordionContent>
                    <table className="w-full zebra-table spec-table">
                      <tbody>
                        <SpecRow label="Model Code" value={specs.model_code} />
                        <SpecRow label="Nominal Capacity" value={`${specs.nominal_capacity_kw} kW / ${specs.nominal_capacity_tr} TR`} />
                        <SpecRow label="Refrigerant" value={specs.refrigerant} />
                        <SpecRow label="Refrigerant Charge" value={specs.refrigerant_charge_kg ? `${specs.refrigerant_charge_kg} kg` : null} />
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="compressor">
                  <AccordionTrigger className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Compressor</AccordionTrigger>
                  <AccordionContent>
                    <table className="w-full zebra-table spec-table">
                      <tbody>
                        <SpecRow label="Type" value={specs.compressor_details?.type} />
                        <SpecRow label="Model" value={specs.compressor_details?.model} />
                        <SpecRow label="Motor Rating" value={specs.compressor_details?.motor_rating_kw ? `${specs.compressor_details.motor_rating_kw} kW` : null} />
                        <SpecRow label="Speed" value={specs.compressor_details?.rpm ? `${specs.compressor_details.rpm} RPM` : null} />
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="heatex">
                  <AccordionTrigger className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Heat Exchangers</AccordionTrigger>
                  <AccordionContent>
                    <table className="w-full zebra-table spec-table">
                      <tbody>
                        <SpecRow label="Evaporator Type" value={specs.evaporator_type} />
                        <SpecRow label="Evaporator Material" value={specs.evaporator_material} />
                        <SpecRow label="Condenser Type" value={specs.condenser_type} />
                        <SpecRow label="Condenser Material" value={specs.condenser_material} />
                        <SpecRow label="Evaporator Flow Rate" value={specs.evaporator_flow_rate ? `${specs.evaporator_flow_rate} m\u00b3/h` : null} />
                        <SpecRow label="Condenser Flow Rate" value={specs.condenser_flow_rate ? `${specs.condenser_flow_rate} m\u00b3/h` : null} />
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="performance">
                  <AccordionTrigger className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Performance & Electrical</AccordionTrigger>
                  <AccordionContent>
                    <table className="w-full zebra-table spec-table">
                      <tbody>
                        <SpecRow label="Chilled Water Outlet" value={specs.design_temps?.chilled_water_outlet} />
                        <SpecRow label="Chilled Water Inlet" value={specs.design_temps?.chilled_water_inlet} />
                        <SpecRow label="Condenser Water Inlet" value={specs.design_temps?.condenser_water_inlet} />
                        <SpecRow label="Condenser Water Outlet" value={specs.design_temps?.condenser_water_outlet} />
                        <SpecRow label="Ambient Air" value={specs.design_temps?.ambient_air} />
                        <SpecRow label="Power Consumption" value={specs.power_consumption_kw ? `${specs.power_consumption_kw} kW` : null} />
                        <SpecRow label="Current" value={specs.current_a ? `${specs.current_a} A` : null} />
                        <SpecRow label="COP" value={specs.cop} />
                        <SpecRow label="EER" value={specs.eer} />
                        <SpecRow label="Noise Level" value={specs.noise_level_dba ? `${specs.noise_level_dba} dB(A)` : null} />
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="physical">
                  <AccordionTrigger className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Dimensions & Connections</AccordionTrigger>
                  <AccordionContent>
                    <table className="w-full zebra-table spec-table">
                      <tbody>
                        <SpecRow label="Length" value={specs.dimensions?.length_mm ? `${specs.dimensions.length_mm} mm` : null} />
                        <SpecRow label="Width" value={specs.dimensions?.width_mm ? `${specs.dimensions.width_mm} mm` : null} />
                        <SpecRow label="Height" value={specs.dimensions?.height_mm ? `${specs.dimensions.height_mm} mm` : null} />
                        <SpecRow label="Weight" value={specs.weight_kg ? `${specs.weight_kg} kg` : null} />
                        <SpecRow label="Chilled Water Connection" value={specs.connections?.chilled_water} />
                        <SpecRow label="Condenser Water Connection" value={specs.connections?.condenser_water} />
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="controls">
                  <AccordionTrigger className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Controls & Safety</AccordionTrigger>
                  <AccordionContent>
                    <table className="w-full zebra-table spec-table">
                      <tbody>
                        <SpecRow label="PLC" value={specs.controls?.plc_model} />
                        <SpecRow label="Display" value={specs.controls?.display} />
                        <SpecRow label="Protocols" value={specs.controls?.protocols?.join(', ')} />
                        <SpecRow label="Safety Devices" value={specs.safety_devices?.join(', ')} />
                      </tbody>
                    </table>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            {/* Tab B: Options */}
            <TabsContent value="options" className="mt-6">
              <h3 className="font-semibold text-base mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Options & Accessories</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(product.options || []).map((opt, i) => (
                  <Card key={i} style={{ background: 'var(--surface-1)' }}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(14,138,138,0.1)' }}>
                        <Settings className="w-4 h-4" style={{ color: 'var(--brand-teal)' }} />
                      </div>
                      <span className="text-sm" style={{ color: 'var(--brand-navy)' }}>{opt}</span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Tab C: Materials */}
            <TabsContent value="materials" className="mt-6">
              <h3 className="font-semibold text-base mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Materials & Standards</h3>
              {product.materials && (
                <div className="space-y-4">
                  <table className="w-full zebra-table spec-table">
                    <tbody>
                      <SpecRow label="Wetted Materials" value={product.materials.wetted_materials} />
                      <SpecRow label="Tube Options" value={product.materials.tube_options?.join(', ')} />
                      <SpecRow label="Paint Specification" value={product.materials.paint_specs} />
                    </tbody>
                  </table>
                  {product.materials.test_standards && (
                    <div>
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--brand-navy)' }}>Test Standards</h4>
                      <div className="flex flex-wrap gap-1">{product.materials.test_standards.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}</div>
                    </div>
                  )}
                  {product.materials.compliance && (
                    <div>
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--brand-navy)' }}>Compliance</h4>
                      <div className="flex flex-wrap gap-1">{product.materials.compliance.map(c => <Badge key={c} variant="outline" className="text-xs">{c}</Badge>)}</div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Tab D: Downloads */}
            <TabsContent value="downloads" className="mt-6">
              <h3 className="font-semibold text-base mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Downloads</h3>
              {(product.product_downloads || []).length > 0 ? (
                <div className="space-y-2">
                  {product.product_downloads.map(dl => (
                    <Card key={dl._id} style={{ background: 'var(--surface-1)' }}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5" style={{ color: 'var(--brand-teal)' }} />
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{dl.title}</p>
                            <p className="text-xs text-muted-foreground">v{dl.version} &bull; {dl.doc_type}</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" asChild data-testid={`product-download-${dl._id}`}>
                          <a href={`${API_URL}/api/downloads/${dl._id}/file`}><Download className="w-3.5 h-3.5 mr-1" />Download</a>
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No downloads available for this product yet.</p>
                  <p className="text-xs mt-1">Visit our <Link to="/downloads" className="underline" style={{ color: 'var(--brand-teal)' }}>Downloads Library</Link> for general documents.</p>
                </div>
              )}
            </TabsContent>

            {/* Tab E: Inquiry */}
            <TabsContent value="inquiry" className="mt-6">
              <h3 className="font-semibold text-base mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Product Inquiry</h3>
              <p className="text-sm text-muted-foreground mb-4">Interested in the {specs.model_code}? Submit a detailed inquiry with your project requirements.</p>
              <Link to={`/request-quote?model=${specs.model_code}&capacity=${specs.nominal_capacity_kw}`}>
                <Button className="text-white" style={{ background: 'var(--brand-teal)' }}>
                  Request Quote for {specs.model_code} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </TabsContent>

            {/* Tab F: Related */}
            <TabsContent value="related" className="mt-6">
              <h3 className="font-semibold text-base mb-4" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Related Products</h3>
              {(product.related_products || []).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {product.related_products.map(rp => (
                    <Link key={rp._id} to={`/products/detail/${rp.slug}`}>
                      <Card className="product-card-hover" style={{ background: 'var(--surface-1)' }}>
                        <CardContent className="p-4">
                          <h4 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{rp.name}</h4>
                          <p className="text-xs text-muted-foreground">{rp.tagline}</p>
                          <div className="mt-2 flex gap-1">
                            <Badge variant="outline" className="text-[10px]">{rp.specs?.nominal_capacity_kw} kW</Badge>
                            <Badge variant="outline" className="text-[10px]">{rp.specs?.refrigerant}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No related products available.</p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
